import { jsx } from "react/jsx-runtime";
import { createForm } from "./core/createForm/createForm.js";
import { useAddFieldValidation as useBaseAddFieldValidation } from "./core/hooks/useAddFieldValidation/useAddFieldValidation.js";
import { useAddFormValidation as useBaseAddFormValidation } from "./core/hooks/useAddFormValidation/useAddFormValidation.js";
import { useForm } from "./core/hooks/useForm.js";
import { useFormApi } from "./core/hooks/useFormApi.js";
import { useFormMeta } from "./core/hooks/useFormMeta.js";
import {
  useOnSubmitForm
} from "./core/hooks/useOnSubmitForm.js";
import {
  useFormField as useBaseFormField
} from "./core/useFormField/useFormField.js";
import { useFormFieldApi as useBaseFormFieldApi } from "./core/useFormField/useFormFieldApi.js";
import { useIsFieldRequired } from "./index.js";
import { useValidateRange } from "./validators/useValidateRange.js";
import { useValidateRegex } from "./validators/useValidateRegex.js";
import { Form as BaseForm } from "./visual/Form/Form.js";
import {
  FormProvider as BaseFormProvider
} from "./visual/FormProvider/FormProvider.js";
import { useCreateForm } from "./visual/FormProvider/useCreateForm.js";
import { ValidationSummary as BaseValidationSummary } from "./visual/ValidationSummary/ValidationSummary.js";
import {
  Checkbox as BaseCheckbox
} from "./visual/fields/Checkbox/Checkbox.js";
import {
  ColorPicker as BaseColorPicker
} from "./visual/fields/ColorPicker/ColorPicker.js";
import {
  CurrencyInput as BaseCurrencyInput
} from "./visual/fields/CurrencyInput/CurrencyInput.js";
import {
  DatePicker as BaseDatePicker
} from "./visual/fields/DatePicker/DatePicker.js";
import {
  DateRangePicker as BaseDateRangePicker
} from "./visual/fields/DateRangePicker/DateRangePicker.js";
import {
  DurationInput as BaseDurationInput
} from "./visual/fields/DurationInput/DurationInput.js";
import {
  DynamicList as BaseDynamicList
} from "./visual/fields/DynamicList/DynamicList.js";
import {
  MultiSelect as BaseMultiSelect
} from "./visual/fields/MultiSelect/MultiSelect.js";
import {
  NumericInput as BaseNumericInput
} from "./visual/fields/NumericInput/NumericInput.js";
import {
  Password as BasePassword
} from "./visual/fields/Password/Password.js";
import {
  RadioGroup as BaseRadioGroup
} from "./visual/fields/RadioGroup/RadioGroup.js";
import { Select as BaseSelect } from "./visual/fields/Select/Select.js";
import {
  SimpleSelect as BaseSimpleSelect
} from "./visual/fields/SimpleSelect/SimpleSelect.js";
import {
  Switch as BaseSwitch
} from "./visual/fields/Switch/Switch.js";
import {
  TextField as BaseTextField
} from "./visual/fields/TextField/TextField.js";
import {
  TimePicker as BaseTimePicker
} from "./visual/fields/TimePicker/TimePicker.js";
function setupFormState(ns, userConfig) {
  const config = structuredClone(userConfig);
  return {
    // ------------------------------
    // CORE ENGINE
    // ------------------------------
    [`create${ns}Form`]: createForm,
    [`use${ns}CreateForm`]: useCreateForm,
    [`use${ns}FormMeta`]: useFormMeta,
    [`use${ns}FormApi`]: useFormApi,
    [`use${ns}Form`]: useForm,
    [`use${ns}FormFieldApi`]: function useFormFieldApi(fieldName) {
      return useBaseFormFieldApi(fieldName);
    },
    [`use${ns}FormField`]: function useFormField(fieldName, options) {
      return useBaseFormField(fieldName, options);
    },
    [`use${ns}AddFormValidation`]: useBaseAddFormValidation,
    [`use${ns}AddFieldValidation`]: function useAddFieldValidation(fieldName, validateField, options) {
      return useBaseAddFieldValidation(fieldName, validateField, options);
    },
    [`use${ns}OnSubmitForm`]: function useAddFieldValidation(submit, config2) {
      return useOnSubmitForm(submit, config2);
    },
    // ------------------------------
    // BUILT-IN VALIDATION HOOKS
    // ------------------------------
    [`use${ns}ValidateRegex`]: function useAddFieldValidation(field, options) {
      return useValidateRegex(field, options);
    },
    [`use${ns}ValidateRange`]: function useAddFieldValidation(field, options) {
      return useValidateRange(field, options);
    },
    [`use${ns}IsFieldRequired`]: function useAddFieldValidation(field, options) {
      return useIsFieldRequired(field, options);
    },
    // ------------------------------
    // UI ROOT COMPONENTS
    // ------------------------------
    [`${ns}FormProvider`]: function FormProvider(props) {
      return /* @__PURE__ */ jsx(BaseFormProvider, { ...props, config });
    },
    [`${ns}Form`]: function Form(props) {
      return /* @__PURE__ */ jsx(BaseForm, { ...props, config });
    },
    // ------------------------------
    // UI FIELDS COMPONENTS
    // ------------------------------
    [`${ns}Checkbox`]: function Checkbox(props) {
      return /* @__PURE__ */ jsx(BaseCheckbox, { ...props });
    },
    [`${ns}ColorPicker`]: function ColorPicker(props) {
      return /* @__PURE__ */ jsx(BaseColorPicker, { ...props });
    },
    [`${ns}DatePicker`]: function DatePicker(props) {
      return /* @__PURE__ */ jsx(BaseDatePicker, { ...props });
    },
    [`${ns}DateRangePicker`]: function DateRangePicker(props) {
      return /* @__PURE__ */ jsx(BaseDateRangePicker, { ...props });
    },
    [`${ns}DynamicList`]: function DynamicList(props) {
      return /* @__PURE__ */ jsx(BaseDynamicList, { ...props });
    },
    [`${ns}DurationInput`]: function DurationInput(props) {
      return /* @__PURE__ */ jsx(BaseDurationInput, { ...props });
    },
    [`${ns}MultiSelect`]: function MultiSelect(props) {
      return /* @__PURE__ */ jsx(BaseMultiSelect, { ...props });
    },
    [`${ns}NumericInput`]: function NumericInput(props) {
      return /* @__PURE__ */ jsx(BaseNumericInput, { ...props });
    },
    [`${ns}Password`]: function Password(props) {
      return /* @__PURE__ */ jsx(BasePassword, { ...props });
    },
    [`${ns}RadioGroup`]: function RadioGroup(props) {
      return /* @__PURE__ */ jsx(BaseRadioGroup, { ...props });
    },
    [`${ns}Select`]: function Select(props) {
      return /* @__PURE__ */ jsx(BaseSelect, { ...props });
    },
    [`${ns}SimpleSelect`]: function SimpleSelect(props) {
      return /* @__PURE__ */ jsx(BaseSimpleSelect, { ...props });
    },
    [`${ns}Switch`]: function Switch(props) {
      return /* @__PURE__ */ jsx(BaseSwitch, { ...props });
    },
    [`${ns}TextField`]: function TextField(props) {
      return /* @__PURE__ */ jsx(BaseTextField, { ...props });
    },
    [`${ns}CurrencyInput`]: function CurrencyInput(props) {
      return /* @__PURE__ */ jsx(BaseCurrencyInput, { ...props });
    },
    [`${ns}TimePicker`]: function TimePicker(props) {
      return /* @__PURE__ */ jsx(BaseTimePicker, { ...props });
    },
    // ------------------------------
    // UI HELPER COMPONENTS
    // ------------------------------
    [`${ns}ValidationSummary`]: function ValidationSummary(props) {
      return /* @__PURE__ */ jsx(BaseValidationSummary, { ...props });
    }
  };
}
export {
  setupFormState
};
//# sourceMappingURL=setupFormState.js.map
